import { useEffect, useRef, useState } from 'react'
import { can, codeWords, refusalReason, type Assessment, type Vendor } from '../../lib/api'
import type { VendorExitRecord } from '../../lib/exit'
import { personLabel, phoneWords, usePeopleContacts } from '../../lib/people'
import { statusWord, vendorStage } from '../../lib/status'
import { Chip } from '../StatusChip'
import { Crumbs } from '../Crumbs'
import { vendorName } from './facts'
import { Highlights } from './Highlights'
import type { Place } from './sections'

// The top of the vendor record: where it sits, who it is and where it stands.
//
// # THREE LINES (release 3.3, the 7 Oct plan section 3.3)
//
// The breadcrumb: the list the vendor was opened from, then the vendor's name,
// not a link (Back model B2 and B4). The header, one line: the name, the code,
// the stage, an exit when one is proposed, and the quieter facts (tier, type,
// owner) after them. The facts, one line under it (Highlights.tsx): the band,
// the findings, the data and the next review. They were a header, a strip of
// four facts and two cards, 513 px before a section began on a 768 px screen.
// The name is never cut short: a long one wraps, and what follows it wraps as
// words do.
//
// # NO BUTTON FOR THE TASK
//
// The header used to carry the questionnaire's verb (Review answers, Chase
// questionnaire, Send questionnaire) and, when an exit was proposed, Decide on
// exit, while the Next steps panel below offered the same tasks again in other
// words (release 3.2 removal items 2 and 8). The task now has one door, the
// next steps on the line under this header (NowLine.tsx), and an exit is
// decided in its workspace and arrives in My work. The header says where the
// vendor stands, in two labelled things that cannot be read as one: the
// vendor's stage, and an exit when one is proposed.

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase() || '?'
}

function MoreMenu({ onEdit, onGo }: { onEdit: () => void; onGo: (p: Place) => void }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        root.current?.querySelector<HTMLButtonElement>('button')?.focus()
      }
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  const pick = (fn: () => void) => () => {
    setOpen(false)
    fn()
  }
  return (
    <div className="vd-more" ref={root}>
      <button type="button" className="ghost" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((v) => !v)}>
        More
      </button>
      {open && (
        <ul className="vd-more-list">
          <li>
            <button
              type="button"
              onClick={pick(onEdit)}
              disabled={!can('vendor.edit')}
              title={can('vendor.edit') ? undefined : refusalReason('vendor.edit')}
            >
              Edit this record
            </button>
          </li>
          <li>
            <button type="button" onClick={pick(() => onGo({ section: 'risk', part: 'reassessment' }))}>
              Reassessment policy
            </button>
          </li>
          {can('audit.read') && (
            <li>
              <button type="button" onClick={pick(() => onGo({ section: 'history', part: null }))}>
                Full history
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

export function VendorHeader({
  vendor,
  assessments,
  liveExit,
  names,
  onGo,
  onEdit,
}: {
  vendor: Vendor
  assessments: Assessment[]
  liveExit: VendorExitRecord | null
  names: Record<string, string>
  onGo: (p: Place) => void
  onEdit: () => void
}) {
  const name = vendorName(vendor)
  const stage = vendorStage(vendor.status, assessments.length > 0, vendor.requestApproved)
  const tier = vendor.tier?.label || ''
  const owner = personLabel(names, vendor.ownerUserId)
  // The owner's work phone, kept in People and roles, beside the address the
  // header already shows (review 3.4). The people read is shared and cached,
  // so this costs no request of its own.
  const ownerPhone = usePeopleContacts()[(vendor.ownerUserId ?? '').trim()]?.phone ?? ''
  // An exit under way is already the stage (Offboarding); a proposal is not,
  // so it is named beside it.
  const proposed = liveExit?.status === 'PROPOSED'

  return (
    <header className="vd-head">
      {/* Where the vendor sits, by name (Back model B2): the list it was
          opened from, with its filters, for this visit (B4, lib/origin.ts),
          "My work" when opened from My work, "Vendors" from a link. */}
      <Crumbs className="vd-crumb" home={{ label: 'Vendors', href: '#/vendors' }} current={name} />
      <div className="vd-head-row">
        <div className="vd-head-main">
          <div className="vd-id">
            <span className="vd-logo" aria-hidden="true">
              {initials(name)}
            </span>
            <div className="vd-id-text">
              <h1>{name}</h1>
              <span className="kit-mono vd-code">{vendor.vendorCode}</span>
              <span className="vd-labelled">
                <span className="vd-label">Stage</span>
                <Chip s={stage} />
              </span>
              {proposed && (
                <span className="vd-labelled">
                  <span className="vd-label">Offboarding</span>
                  <Chip s={statusWord('exit', 'PROPOSED')} />
                </span>
              )}
              <span className="vd-head-facts">
                {tier && <span>{tier}</span>}
                {vendor.vendorType && <span>{codeWords(vendor.vendorType)}</span>}
                <span>
                  Owner {owner || 'unassigned'}
                  {owner && ownerPhone && (
                    <>
                      {' '}
                      <a href={'tel:' + ownerPhone} aria-label={'Owner phone ' + phoneWords(ownerPhone)}>
                        {phoneWords(ownerPhone)}
                      </a>
                    </>
                  )}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="vd-actions">
          <MoreMenu onEdit={onEdit} onGo={onGo} />
        </div>
      </div>
      <Highlights vendor={vendor} />
    </header>
  )
}
